import { findAllWordSequenceMatches, type MatchableWord } from './transcript-word-match';

export type TimedWord = MatchableWord & { startTime: number; endTime: number };

/** Longer than any short clip needs: a segment this long almost always means end_quote matched a later line. */
export const MAX_CLIP_SEGMENT_MS = 3 * 60 * 1000;

export interface ClipSegmentQuotes {
    start_quote: string;
    end_quote: string;
}

export type ClipSegmentResolution =
    | { ok: true; startMs: number; endMs: number }
    | { ok: false; error: string };

export type QuotedSpan =
    | { ok: true; startIndex: number; endIndex: number }
    | { ok: false; error: string };

/**
 * The transcript word indices a segment spans, from the words it opens and closes on. The opening
 * quote must be unique in the recording; the segment ends at the first occurrence of the closing
 * quote that finishes at or after the opening one does.
 */
export function locateQuotedSpan(
    transcriptWords: readonly MatchableWord[],
    { start_quote, end_quote }: ClipSegmentQuotes,
): QuotedSpan {
    const starts = findAllWordSequenceMatches(transcriptWords, [start_quote]);
    if (starts.length === 0) {
        return { ok: false, error: `start_quote "${start_quote}" is not in the transcript` };
    }
    if (starts.length > 1) {
        return {
            ok: false,
            error: `start_quote "${start_quote}" appears ${starts.length} times in the transcript; quote more words so it is unique`,
        };
    }
    const [start] = starts;
    const end = findAllWordSequenceMatches(transcriptWords, [end_quote]).find(
        (match) => match.endIndex >= start.endIndex,
    );
    if (!end) {
        return {
            ok: false,
            error: `end_quote "${end_quote}" does not appear at or after start_quote "${start_quote}"`,
        };
    }
    return { ok: true, startIndex: start.startIndex, endIndex: end.endIndex };
}

/**
 * Locate a clip segment from the transcript words it opens and closes on (see locateQuotedSpan).
 * Both edges land on word timings, so the segment starts and ends on a word. A segment longer than
 * MAX_CLIP_SEGMENT_MS is rejected rather than silently spanning half the recording.
 */
export function resolveClipSegment(
    transcriptWords: readonly TimedWord[],
    quotes: ClipSegmentQuotes,
): ClipSegmentResolution {
    const span = locateQuotedSpan(transcriptWords, quotes);
    if (!span.ok) {
        return span;
    }
    const startMs = transcriptWords[span.startIndex].startTime;
    const endMs = transcriptWords[span.endIndex].endTime;
    if (endMs - startMs > MAX_CLIP_SEGMENT_MS) {
        return {
            ok: false,
            error: `runs ${Math.round((endMs - startMs) / 1000)}s from start_quote "${quotes.start_quote}" to the first "${quotes.end_quote}" after it, longer than the ${MAX_CLIP_SEGMENT_MS / 1000}s a segment can be; quote the line the segment actually ends on`,
        };
    }
    return { ok: true, startMs, endMs };
}
